import { useState } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  Alert
} from '@mui/material';
import { ModalAjustes } from './ModalesInventario';
import { formatearFechaLocal, normalizarFechaISO } from '../../utils/horaLocal';

export default function HistorialAjustes({ state }) {
  const [openModal, setOpenModal] = useState(false);

  const ajustes = state.obtenerAjustesInclusoAnulados() || [];
  const díaBloqueado = state?.verificarDíaBloqueado();
  const arqueoDelDía = state?.obtenerArqueoDelDía();

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-BO', {
      style: 'currency',
      currency: 'BOB'
    }).format(monto);
  };

  const handleAbrirModal = () => {
    setOpenModal(true);
  };

  const handleCerrarModal = () => {
    setOpenModal(false);
  };

  if (ajustes.length === 0) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight="bold">
            Historial de Ajustes de Caja
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={handleAbrirModal}
            disabled={díaBloqueado}
          >
            + Nuevo Ajuste
          </Button>
        </Stack>

        {díaBloqueado && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            🔒 No puedes crear ajustes. El arqueo de caja fue completado hoy a las {new Date(arqueoDelDía.fecha).toLocaleTimeString('es-BO')}.
          </Alert>
        )}

        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            No hay ajustes registrados
          </Typography>
        </Box>

        <ModalAjustes
          open={openModal}
          onClose={handleCerrarModal}
          state={state}
          díaBloqueado={díaBloqueado}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          Historial de Ajustes de Caja
        </Typography>
        <Button
          variant="contained"
          color="success"
          onClick={handleAbrirModal}
          disabled={díaBloqueado}
        >
          + Nuevo Ajuste
        </Button>
      </Stack>

      {díaBloqueado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          🔒 No puedes crear ajustes. El arqueo de caja fue completado hoy a las {new Date(arqueoDelDía.fecha).toLocaleTimeString('es-BO')}.
        </Alert>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <TableCell>Fecha</TableCell>
              <TableCell>Hora</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ajustes.map(ajuste => (
              <TableRow key={ajuste.id}>
                <TableCell>
                  {formatearFechaLocal(normalizarFechaISO(ajuste.fecha))}
                </TableCell>
                <TableCell>
                  {new Date(ajuste.fecha).toLocaleTimeString('es-BO', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={ajuste.tipo}
                    color={ajuste.tipo === 'SOBRANTE' || ajuste.tipo === 'INYECCIÓN' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <strong>{formatearMonto(ajuste.monto)}</strong>
                </TableCell>
                <TableCell>
                  {ajuste.descripcion || '-'}
                </TableCell>
                <TableCell align="center">
                  {state.esAjusteAnulado(ajuste) ? (
                    <Chip
                      label="ANULADO"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label="ACTIVO"
                      size="small"
                      color="success"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* RESUMEN */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Stack direction="row" spacing={4} justifyContent="flex-start">
          <Box>
            <Typography variant="body2" color="textSecondary">
              Total Sobrantes
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              {formatearMonto(
                ajustes
                  .filter(a => a.tipo === 'SOBRANTE' && !state.esAjusteAnulado(a))
                  .reduce((sum, a) => sum + a.monto, 0)
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Total Inyecciones
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              {formatearMonto(
                ajustes
                  .filter(a => a.tipo === 'INYECCIÓN' && !state.esAjusteAnulado(a))
                  .reduce((sum, a) => sum + a.monto, 0)
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Total Faltantes
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
              {formatearMonto(
                ajustes
                  .filter(a => a.tipo === 'FALTANTE' && !state.esAjusteAnulado(a))
                  .reduce((sum, a) => sum + a.monto, 0)
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Neto (Sobrante + Inyección - Faltante)
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: ajustes.filter(a => !state.esAjusteAnulado(a)).reduce((sum, a) => {
                  return sum + (a.tipo === 'SOBRANTE' || a.tipo === 'INYECCIÓN' ? a.monto : -a.monto);
                }, 0) >= 0 ? '#2e7d32' : '#d32f2f'
              }}
            >
              {formatearMonto(
                ajustes.filter(a => !state.esAjusteAnulado(a)).reduce((sum, a) => {
                  return sum + (a.tipo === 'SOBRANTE' || a.tipo === 'INYECCIÓN' ? a.monto : -a.monto);
                }, 0)
              )}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <ModalAjustes
        open={openModal}
        onClose={handleCerrarModal}
        state={state}
        díaBloqueado={díaBloqueado}
      />
    </Paper>
  );
}
